import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./About.css";
import AdmissionDropdown from "../components/AdmissionDropdown";

const About = () => {
  const [selectedLeader, setSelectedLeader] = useState(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setSelectedLeader(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedLeader ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedLeader]);

  const leaders = [
    {
      name: "Shri. R S Hanumantha Rao",
      position: "Founder, Sri Vani Education Centre Trust",
      degree: "M.Sc. Physics",
      lifespan: "1929 – 1972",
      image: "/images/hanumantha-rao.jpg",
      bio: {
        tagline: "True grit and vision decimates adversity.",
        illustrationVideo: "/videos/founder-line-drawing.mp4",
        illustrationVideoStart: 27,
        illustrationImage: "/images/hanumantha-rao-illustration.jpg",
        illustrationFallback: "/images/hanumantha-rao-illustration.jpg",
        earlyLifeImage: "/images/founder-portrait-2.jpg",
        earlyLife:
          "Born in 1929, Shri R S Hanumantha Rao faced profound hardship from the age of three following the death of his father, hotelier Shri R Shamanna. His uncles defrauded his illiterate mother of the family's assets, forcing young Hanumantha Rao to work as a waiter in his father's hotel even as he continued pursuing his own education. This early adversity forged in him an unbreakable resolve and a lifelong mission to make quality education accessible to all, regardless of background.",
        accomplishments: [
          {
            title: "Sree Vinayaka Education Society (1964)",
            image: "/images/founder-vinayaka-1964.jpg",
            description:
              "He founded this school at Cubbonpet, but was compelled to surrender it due to prevailing caste discrimination — a setback that only deepened his determination to build something lasting.",
          },
          {
            title: "Sri Vani Education Centre (1966)",
            image: "/images/founder-sri-vani-1966.jpg",
            description:
              "Starting with just 18 students, his elaborate planning and result-oriented working abilities grew the institution to serve over 5,500 students. This became the cornerstone of what is today Sri Vani Education.",
          },
          {
            title: "Atmarama High School",
            image: "/images/founder-atmarama.jpg",
            description:
              "Responding to requests from Narasipura residents, he established this school despite severe financial constraints so that rural children would not be denied access to education.",
          },
          {
            title: "Sanskriti Prachar Samiti",
            image: "/images/founder-sanskriti.jpg",
            description:
              "A cultural organisation dedicated to preserving Hindu customs and Indian heritage, reflecting his belief that education must nurture values alongside academics.",
          },
        ],
        passingTheBaton: {
          image: "/images/founder-passing-baton.jpg",
          text: "In 1972, Mr. Rao's sudden demise due to a massive heart attack brought an end to a great era. His wife, Smt. RH Usha, successfully took over the institution's administration with the help of her five children & brothers. Sri Vani is now hailed as one of Bengaluru's most prestigious educational institutes.",
        },
      },
    },
    {
      name: "Smt R H Usha",
      position: "President, Sri Vani Education Centre Trust",
      degree: "SSLC, Graduate",
      image: "/images/usha-photo.jpg",
      bio: {
        type: "profile",
        portraitLarge: "/images/trustee-usha-secondary.jpg",
        portraitSecondary: "/images/trustee-usha-large.jpg",
        description:
          "Wife of the late Founder Shri R.S. Hanumantha Rao, Smt. R H Usha completed only fifth grade at the time of her marriage. Undeterred by circumstance, she pursued education alongside raising five children and eventually graduated later in life — a testament to the same grit and determination that defined her husband. Following his sudden passing in 1972, she stepped forward to lead Sri Vani Education with unwavering resolve, supported by her five children and brothers. Under her stewardship, Sri Vani has grown into one of Bengaluru's most prestigious educational institutions.",
      },
    },
    {
      name: "Shri Sharada Prasad RH",
      position: "Managing Trustee & Principal",
      degree: "Ed.S., M.Ed., B.Ed., B.Sc., SAS (Business Intelligence), CISCO",
      image: "/images/sharada-photo.png",
      bio: {
        type: "profile",
        portraitLarge: "/images/trustee-sharada-large.png",
        description:
          "Eldest son of Founder Shri R.S. Hanumantha Rao, Shri Sharada Prasad RH brings over thirty years of experience spanning international education and hospitality. A self-described educator and hotelier, he is deeply committed to establishing world-class education rooted in Sanatana Dharma values. He uses his father's vision as a directional guide for the continued advancement of Sri Vani, bridging tradition with modern pedagogical excellence.",
      },
    },
    {
      name: "Smt Nalini S Prasad",
      position: "Treasurer & Trustee",
      degree:
        "B.Sc (Nutrition & Home Sciences), Diploma in Montessori Training, Diploma in Interior Decoration",
      image: "/images/nalini-photo.jpg",
      bio: {
        type: "profile",
        portraitLarge: "/images/trustee-nalini-large.png",
        description:
          "A skilled dietitian with 26 years of healthcare experience — including a distinguished tenure at the University of Kansas Medical Centre — Smt. Nalini S Prasad brings a wealth of expertise in nutrition, early childhood education, and interior design. As former Headmistress of the Nursery and Early Childhood Education division at the Shamavana campus during its formative years, she played an instrumental role in shaping the foundational learning environment of Sri Vani.",
      },
    },
  ];

  const milestones = [
    {
      year: "1966",
      event: "Sri Vani Education founded in Rajajinagar, Bangalore",
    },
    { year: "1975", event: "Expanded to ICSE curriculum" },
    { year: "1985", event: "Opened second campus in Basaveshwaranagar" },
    { year: "1995", event: "Introduced CBSE curriculum" },
    { year: "2005", event: "Launched Cambridge International program" },
    { year: "2010", event: "Established Macholli campus" },
    {
      year: "2015",
      event: "Opened Naavruja campus with state-of-the-art facilities",
    },
    { year: "2020", event: "Implemented digital learning across all campuses" },
    { year: "2024", event: "Celebrating 58 years of educational excellence" },
  ];

  const awards = [
    {
      title: "All India Radio Awards — Choral Group Singing",
      year: "1989",
      organization: "All India Radio (AIR)",
      description:
        "One of the numerous All India Radio Awards won by Vani students for Choral Group Singing, accepted by Smt. R H Usha on behalf of the School. The ACT Award at Times Playfest was also won in November 1997, conducted by The Times of India Group.",
      image: "/images/award-air-choral-1989.jpg",
      featured: true,
    },
    {
      title: "Presidential Felicitation - Choral Singing Group",
      year: "1986",
      organization: "President Zail Singh",
      description:
        "Students of the Choral Singing Group recognised after their award-winning performance at AIR Awards",
      image: "/images/felicitated-presidet-zail-singh.jpg",
    },
    {
      title: "Presidential Recognition - World Environment Day",
      year: "2005",
      organization: "President Dr. APJ Abdul Kalam",
      description:
        "Rakshitha S (V Std) received First Prize trophy for painting on World Environment Day",
      image: "/images/Sri-Vani-APJ-trophy.jpg",
    },
    {
      title: "National Award For Teachers",
      year: "2005",
      organization: "Ministry of Human Resource Development",
      description:
        "Shri Shama Sundara Sharma (then Principal) bestowed the National Award for Teachers for valuable services and outstanding merit",
      image: "/images/national-award-teacher.jpg",
    },
    {
      title: "Vani Achievers",
      year: "2019-2020",
      organization: "Multiple Awards & Recognition",
      description:
        "2 KAR AIR SQN (T) NCC won the Best Institution Award in 2020. Vaniites also won Skating Championship (Mr. Akhil, Mr. Darshan & Mrs. Yashaswini) and Table Tennis Runners Up (Ms. Harini & Ms. Anagha) at the 2019-20 ICSE Meet",
      image: "/images/achievers-trophies.jpg",
    },
    {
      title: "National Painting Competition",
      year: "2019",
      organization: "National Level Competition",
      description:
        "Students awarded in National Painting Competition including Jeevika G Gowda, Shreeya M, B R Varsha, and Kishan Shetty",
      image: "/images/painting-comp.jpeg",
    },
    {
      title: "Young Gandhian Award - National Rank #01",
      year: "2021",
      organization: "National Level Young Gandhian Competition",
      description:
        "NCC Cadet Shraya Girish from Basaveshwaranagar campus secured National Rank #01 with cash prize of Rs.40,100/-",
      image: "/images/young-gandhiyan-award.jpg",
    },
  ];

  return (
    <div className="about-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>About Sri Vani Education</h1>
            <p>Excellence in Education Since 1966</p>
          </motion.div>
        </div>
      </section>

      {/* 58-Year Legacy Section */}
      <section className="legacy-section section" id="legacy">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">
              {new Date().getFullYear() - 1966}-Year Legacy (Since 1966)
            </h2>
            <p className="section-subtitle">
              Over five decades of educational excellence and transforming lives
            </p>
          </motion.div>

          <div className="legacy-content">
            <motion.div
              className="legacy-intro"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p>
                Founded in 1966, Sri Vani Education has been a beacon of quality
                education in Bangalore for over five decades. What started as a
                small school in Rajajinagar has grown into a prestigious
                educational institution with four campuses, serving over 5,000
                students across multiple curricula.
              </p>
              <p>
                Through the years, we have remained committed to our founding
                principles of providing affordable, world-class education while
                nurturing traditional values and character development.
              </p>
            </motion.div>

            {/* Timeline */}
            <div className="timeline">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  className={`timeline-item ${index % 2 === 0 ? "left" : "right"}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="timeline-content card">
                    <div className="timeline-year">{milestone.year}</div>
                    <p>{milestone.event}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team Section */}
      <section className="leadership-section section" id="leadership">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Leadership Team</h2>
            <p className="section-subtitle">
              Experienced educators committed to excellence and student success
            </p>
          </motion.div>

          <div className="leaders-grid">
            {leaders.map((leader, index) => (
              <motion.div
                key={index}
                className={`leader-card card${leader.bio ? " leader-card--clickable" : ""}`}
                onClick={() => leader.bio && setSelectedLeader(leader)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="leader-image">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentElement.setAttribute(
                        "data-initials",
                        leader.name.charAt(0),
                      );
                      e.target.parentElement.classList.add(
                        "leader-image--fallback",
                      );
                    }}
                  />
                </div>
                <div className="leader-info">
                  <h3>{leader.name}</h3>
                  {leader.lifespan && (
                    <p className="leader-lifespan">{leader.lifespan}</p>
                  )}
                  <p className="leader-position">{leader.position}</p>
                  <p className="leader-degree">{leader.degree}</p>
                  {leader.bio && (
                    <span className="leader-read-more">View Profile →</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards & Recognition Section */}
      <section className="awards-section section" id="awards">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Awards & Recognition</h2>
            <p className="section-subtitle">
              Honored for excellence in education and community service
            </p>
          </motion.div>

          <div className="awards-grid">
            {awards.map((award, index) =>
              award.featured ? (
                <motion.div
                  key={index}
                  className="award-card--featured"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="award-featured-image">
                    <img src={award.image} alt={award.title} />
                  </div>
                  <div className="award-featured-content">
                    <h3>{award.title}</h3>
                    <p className="award-year">{award.year}</p>
                    <p className="award-org">{award.organization}</p>
                    {award.description && (
                      <p className="award-description">{award.description}</p>
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={index}
                  className="award-card card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {award.image && (
                    <div className="award-image">
                      <img src={award.image} alt={award.title} />
                    </div>
                  )}
                  <h3>{award.title}</h3>
                  <p className="award-year">{award.year}</p>
                  <p className="award-org">{award.organization}</p>
                  {award.description && (
                    <p className="award-description">{award.description}</p>
                  )}
                </motion.div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* Virtual Campus Tour Section */}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Join the Sri Vani Family</h2>
            <p>Discover how we can shape your child's future</p>
            <div className="cta-buttons">
              <AdmissionDropdown label="Apply for Admission" className="btn btn-secondary" />
              {/* <Link to="/contact" className="btn btn-secondary">
                Schedule a Visit
              </Link> */}
            </div>
          </motion.div>
        </div>
      </section>
      {/* Leader Bio Modal */}
      {selectedLeader && (
        <div
          className="leader-modal-overlay"
          onClick={() => setSelectedLeader(null)}
        >
          <motion.div
            className={`leader-modal${selectedLeader.bio?.type === "profile" ? " leader-modal--profile" : ""}`}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <button
              className="modal-close"
              onClick={() => setSelectedLeader(null)}
              aria-label="Close"
            >
              ×
            </button>

            {selectedLeader.bio?.type === "profile" ? (
              /* ── Trustee profile layout ── */
              <div className="modal-profile">
                <div className="modal-profile-images">
                  <img
                    className="modal-profile-portrait"
                    src={selectedLeader.bio.portraitLarge}
                    alt={selectedLeader.name}
                    onError={(e) => {
                      e.target.src = selectedLeader.image;
                    }}
                  />
                </div>
                <div className="modal-profile-content">
                  <h2>{selectedLeader.name}</h2>
                  <p className="modal-position">{selectedLeader.position}</p>
                  <p className="modal-degree">{selectedLeader.degree}</p>
                  <div className="modal-profile-divider" />
                  <p className="modal-profile-bio">
                    {selectedLeader.bio.description}
                  </p>
                </div>
              </div>
            ) : (
              /* ── Founder full layout ── */
              <>
                {(selectedLeader.bio?.illustrationVideo ||
                  selectedLeader.bio?.illustrationImage) && (
                  <div className="modal-illustration">
                    {selectedLeader.bio.illustrationVideo && (
                      <video
                        className="modal-illustration-video"
                        autoPlay
                        muted
                        loop
                        playsInline
                        onLoadedMetadata={(e) => {
                          e.target.currentTime =
                            selectedLeader.bio.illustrationVideoStart || 0;
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      >
                        <source
                          src={selectedLeader.bio.illustrationVideo}
                          type="video/mp4"
                        />
                      </video>
                    )}
                    {selectedLeader.bio.illustrationImage && (
                      <img
                        className="modal-illustration-img"
                        src={selectedLeader.bio.illustrationImage}
                        alt={`${selectedLeader.name} illustration`}
                        onError={(e) => {
                          e.target.src =
                            selectedLeader.bio.illustrationFallback;
                          e.target.onerror = () => {
                            e.target.style.display = "none";
                          };
                        }}
                      />
                    )}
                  </div>
                )}

                <div className="modal-header">
                  <div className="modal-portrait">
                    <img
                      src={selectedLeader.image}
                      alt={selectedLeader.name}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.setAttribute(
                          "data-initials",
                          selectedLeader.name.charAt(0),
                        );
                        e.target.parentElement.classList.add(
                          "leader-image--fallback",
                        );
                      }}
                    />
                  </div>
                  <div className="modal-identity">
                    <h2>{selectedLeader.name}</h2>
                    {selectedLeader.lifespan && (
                      <p className="modal-lifespan">
                        {selectedLeader.lifespan}
                      </p>
                    )}
                    <p className="modal-position">{selectedLeader.position}</p>
                    <p className="modal-degree">{selectedLeader.degree}</p>
                    {selectedLeader.bio?.tagline && (
                      <blockquote className="modal-tagline">
                        "{selectedLeader.bio.tagline}"
                      </blockquote>
                    )}
                  </div>
                </div>

                <div className="modal-body">
                  {selectedLeader.bio?.earlyLife && (
                    <div className="modal-section">
                      <h3>Early Life</h3>
                      <div className="modal-section-with-image">
                        {selectedLeader.bio.earlyLifeImage && (
                          <div className="modal-section-image">
                            <img
                              src={selectedLeader.bio.earlyLifeImage}
                              alt="Early life"
                              onError={(e) => {
                                e.target.closest(
                                  ".modal-section-image",
                                ).style.display = "none";
                              }}
                            />
                          </div>
                        )}
                        <p>{selectedLeader.bio.earlyLife}</p>
                      </div>
                    </div>
                  )}

                  {selectedLeader.bio?.accomplishments?.length > 0 && (
                    <div className="modal-section">
                      <h3>Major Accomplishments</h3>
                      <div className="modal-accomplishments">
                        {selectedLeader.bio.accomplishments.map((item, i) => (
                          <div key={i} className="accomplishment-item">
                            {item.image && (
                              <div className="accomplishment-image">
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  onError={(e) => {
                                    e.target.closest(
                                      ".accomplishment-image",
                                    ).style.display = "none";
                                  }}
                                />
                              </div>
                            )}
                            <div className="accomplishment-text">
                              <h4>{item.title}</h4>
                              <p>{item.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedLeader.bio?.passingTheBaton && (
                    <div className="modal-section">
                      <h3>Passing on the Baton</h3>
                      <div className="modal-section-with-image">
                        {selectedLeader.bio.passingTheBaton.image && (
                          <div className="modal-section-image">
                            <img
                              src={selectedLeader.bio.passingTheBaton.image}
                              alt="Passing on the Baton"
                              onError={(e) => {
                                e.target.closest(
                                  ".modal-section-image",
                                ).style.display = "none";
                              }}
                            />
                          </div>
                        )}
                        <p>{selectedLeader.bio.passingTheBaton.text}</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default About;
